/**
 * ExploreUnknownBossWeaponsRandomizerOverlay
 *
 * Boss-mission only UI. Renders a centered weapons_randomizer.png on top of
 * the battlefield once `gameState.bossWeaponsRandomizerActive === true` (set
 * server-side by `applyBossWeaponsRandomizerOnTurnStart` in
 * `modules/exploreUnknownBossWeaponsRandomizer.js`).
 *
 * For each new `eu-boss-weapons-randomizer-fired` entry in the action log,
 * the component:
 *   1. Looks up the target slot DOM rect via `data-slot-zone` / `data-slot-index`.
 *   2. Rotates the centered image toward that slot (CSS transform).
 *   3. Spawns a `weapons_box.png` `<img>` that arcs from the image to the
 *      slot center, then fades out. The weapon itself is already attached
 *      server-side, so the weapon icon will appear on the unit naturally
 *      via the next `gameState` update.
 *   4. Honors `prefers-reduced-motion`: skip rotation/translation and just
 *      flash the box briefly at the slot center.
 *
 * Action-log entries are deduped via a ref-backed `Set` of sequence keys
 * (mirrors the `weapon-equipped` watcher already in BattleUI.js so we don't
 * replay the same animation if React re-renders).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './ExploreUnknownBossWeaponsRandomizerOverlay.css';

const RANDOMIZER_IMAGE_SRC = '/images/weapons_randomizer.png';
const WEAPONS_BOX_IMAGE_SRC = '/images/weapons_box.png';

const ROTATION_DURATION_MS = 400;
const BOX_TRAVEL_DURATION_MS = 600;
const BOX_FADE_DURATION_MS = 250;
const TOTAL_ANIMATION_MS = ROTATION_DURATION_MS + BOX_TRAVEL_DURATION_MS + BOX_FADE_DURATION_MS + 200;

const SEEN_KEY_HARD_CAP = 250;
const SEEN_KEY_SOFT_CAP = 100;

function actionLogEntryKey(entry) {
  if (!entry) return 'no-entry';
  return `${entry.sequence ?? 'no-seq'}:${entry.timestamp ?? 'no-ts'}:${entry.weaponInstanceId ?? entry.targetUnitInstanceId ?? 'no-instance'}`;
}

function clientSlotZoneFor(entry, userId) {
  if (!entry || !entry.targetZone) return null;
  const isLocalOwner = String(entry.ownerPlayerId) === String(userId);
  const prefix = isLocalOwner ? 'your_' : 'opponent_';
  return `${prefix}${entry.targetZone}`;
}

function findSlotElement(entry, userId) {
  if (typeof document === 'undefined' || !document.querySelector) return null;
  const slotZone = clientSlotZoneFor(entry, userId);
  if (!slotZone) return null;
  return document.querySelector(
    `[data-slot-zone="${slotZone}"][data-slot-index="${entry.targetSlotIndex}"]`
  );
}

function findBattlefieldRect() {
  if (typeof document === 'undefined' || !document.querySelector) return null;
  const el =
    document.querySelector('.battlefield-with-cross') ||
    document.querySelector('.battlefield-container');
  return el ? el.getBoundingClientRect() : null;
}

function rectCenter(rect) {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function detectReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
}

export default function ExploreUnknownBossWeaponsRandomizerOverlay({ gameState, userId }) {
  const { t } = useTranslation();

  const isActive = !!(gameState && gameState.bossWeaponsRandomizerActive);

  const seenKeysRef = useRef(new Set());
  const cleanupTimersRef = useRef(new Set());
  const nextAnimIdRef = useRef(1);

  const [animationItems, setAnimationItems] = useState([]);
  const [imageRotationDeg, setImageRotationDeg] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(detectReducedMotion);
  const [, setRectTick] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    let mq;
    try {
      mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch (_) {
      return undefined;
    }
    const handler = (e) => setReducedMotion(!!e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    if (typeof mq.addListener === 'function') {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setRectTick((tick) => tick + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    return () => {
      for (const timerId of cleanupTimersRef.current) {
        clearTimeout(timerId);
      }
      cleanupTimersRef.current.clear();
    };
  }, []);

  const scheduleAnimationCleanup = useCallback((animId) => {
    const timerId = setTimeout(() => {
      setAnimationItems((prev) => prev.filter((item) => item.id !== animId));
      cleanupTimersRef.current.delete(timerId);
    }, TOTAL_ANIMATION_MS);
    cleanupTimersRef.current.add(timerId);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const log = Array.isArray(gameState?.actionLog) ? gameState.actionLog : [];
    if (log.length === 0) return;

    const fireEntries = log.filter(
      (e) => e && e.type === 'eu-boss-weapons-randomizer-fired'
    );
    if (fireEntries.length === 0) return;

    let latestRotation = null;
    const newAnims = [];

    for (const entry of fireEntries) {
      const key = actionLogEntryKey(entry);
      if (seenKeysRef.current.has(key)) continue;
      seenKeysRef.current.add(key);

      const slotEl = findSlotElement(entry, userId);
      const battlefieldRect = findBattlefieldRect();
      if (!slotEl || !battlefieldRect) continue;

      const slotRect = slotEl.getBoundingClientRect();
      const slotCenter = rectCenter(slotRect);
      const imageCenter = rectCenter(battlefieldRect);
      const imageDim = Math.max(96, Math.min(180, battlefieldRect.width * 0.16));

      const dx = slotCenter.x - imageCenter.x;
      const dy = slotCenter.y - imageCenter.y;
      // The randomizer PNG has its gap (mouth) on the RIGHT edge (east, 0°). Rotating by
      // atan2(dy, dx) degrees — without any +90 offset — aligns that gap with the target
      // slot, so the box visibly exits through the mouth toward the unit being targeted.
      const angleRad = Math.atan2(dy, dx);
      const angleDeg = angleRad * (180 / Math.PI);

      // Spawn the weapons box at the rim of the gap (just inside the mouth) so the box
      // appears to be spit out, rather than teleporting from the dead center of the image.
      // 0.42 ≈ ~84% of the half-extent (because the PNG has some transparent padding).
      const gapRadius = imageDim * 0.42;
      const boxStartX = imageCenter.x + Math.cos(angleRad) * gapRadius;
      const boxStartY = imageCenter.y + Math.sin(angleRad) * gapRadius;

      const animId = nextAnimIdRef.current;
      nextAnimIdRef.current += 1;

      newAnims.push({
        id: animId,
        boxStartX,
        boxStartY,
        slotX: slotCenter.x,
        slotY: slotCenter.y,
        weaponName: entry.weaponName,
      });
      latestRotation = angleDeg;
    }

    if (newAnims.length > 0) {
      setAnimationItems((prev) => [...prev, ...newAnims]);
      newAnims.forEach((item) => scheduleAnimationCleanup(item.id));
    }
    if (latestRotation != null) {
      setImageRotationDeg(latestRotation);
    }

    if (seenKeysRef.current.size > SEEN_KEY_HARD_CAP) {
      const recent = fireEntries.slice(-SEEN_KEY_SOFT_CAP).map(actionLogEntryKey);
      seenKeysRef.current = new Set(recent);
    }
  }, [gameState, userId, isActive, scheduleAnimationCleanup]);

  if (!isActive) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  const battlefieldRect = findBattlefieldRect();
  if (!battlefieldRect) return null;
  const center = rectCenter(battlefieldRect);
  const imageDimension = Math.max(96, Math.min(180, battlefieldRect.width * 0.16));

  return createPortal(
    <div
      className="explore-unknown-boss-weapons-randomizer-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 15080,
      }}
      aria-hidden={false}
    >
      <img
        src={RANDOMIZER_IMAGE_SRC}
        alt={t('exploreUnknownPage.bossWeaponsRandomizerAriaLabel')}
        className={`explore-unknown-boss-weapons-randomizer-image${reducedMotion ? ' is-reduced-motion' : ''}`}
        style={{
          position: 'fixed',
          left: `${center.x}px`,
          top: `${center.y}px`,
          width: `${imageDimension}px`,
          height: `${imageDimension}px`,
          transform: `translate(-50%, -50%) rotate(${reducedMotion ? 0 : imageRotationDeg}deg)`,
          transition: reducedMotion ? 'none' : `transform ${ROTATION_DURATION_MS}ms ease-out`,
          transformOrigin: 'center',
        }}
        draggable={false}
      />
      {animationItems.map((item) => {
        const dx = item.slotX - item.boxStartX;
        const dy = item.slotY - item.boxStartY;
        const animationName = reducedMotion
          ? 'explore-unknown-boss-weapons-randomizer-box-flash'
          : 'explore-unknown-boss-weapons-randomizer-box-arc';
        return (
          <img
            key={item.id}
            src={WEAPONS_BOX_IMAGE_SRC}
            alt=""
            aria-hidden="true"
            className="explore-unknown-boss-weapons-randomizer-box"
            draggable={false}
            style={{
              position: 'fixed',
              left: `${item.boxStartX}px`,
              top: `${item.boxStartY}px`,
              width: '64px',
              height: '64px',
              transform: 'translate(-50%, -50%)',
              animation: `${animationName} ${BOX_TRAVEL_DURATION_MS + BOX_FADE_DURATION_MS}ms cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards`,
              animationDelay: reducedMotion ? '0ms' : `${ROTATION_DURATION_MS}ms`,
              ['--eu-boss-box-dx']: `${dx}px`,
              ['--eu-boss-box-dy']: `${dy}px`,
            }}
          />
        );
      })}
    </div>,
    document.body
  );
}
